import React, { useEffect, useMemo, useState } from "react";
import { cincApi, api, API } from "@/lib/api";
import { COLOR_HEX, ALL_COLORS } from "./colors";

const fmt = (n) => (Number.isInteger(n) ? n : Number(n).toFixed(2)).toString();

const Swatch = ({ color, size = 20 }) => (
  <span style={{ background: COLOR_HEX[color] || "#ccc", width: size, height: size, borderColor: color === "White" ? "#e5e5e5" : "transparent" }} className="inline-block rounded-full border shrink-0" />
);

const CincAdmin = () => {
  const [authed, setAuthed] = useState(!!localStorage.getItem("cinc_token"));
  const [password, setPassword] = useState("");
  const [loginErr, setLoginErr] = useState("");
  const [tab, setTab] = useState("inventory");
  const [items, setItems] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [summary, setSummary] = useState(null);
  const [edits, setEdits] = useState({});
  const [newItem, setNewItem] = useState({ fabric: "", color: ALL_COLORS[0], available_kg: "100" });
  const [threshold, setThreshold] = useState(10);
  const [thresholdInput, setThresholdInput] = useState("10");
  const [bookingView, setBookingView] = useState("active");

  const load = async () => {
    const [it, sm] = await Promise.all([api.get("/cinc/items"), api.get("/cinc/summary")]);
    setItems(it.data);
    setSummary(sm.data);
    if (sm.data.low_stock_kg != null) {
      setThreshold(sm.data.low_stock_kg);
      setThresholdInput(String(sm.data.low_stock_kg));
    }
    try {
      const bk = await cincApi.get("/cinc/bookings");
      setBookings(bk.data);
    } catch {
      /* token expired */
      if (authed) doLogout();
    }
  };

  useEffect(() => {
    if (authed) load();
    // eslint-disable-next-line
  }, [authed]);

  const doLogin = async (e) => {
    e.preventDefault();
    setLoginErr("");
    try {
      const { data } = await api.post("/cinc/admin/login", { password });
      localStorage.setItem("cinc_token", data.token);
      setAuthed(true);
    } catch {
      setLoginErr("Incorrect password");
    }
  };
  const doLogout = () => {
    localStorage.removeItem("cinc_token");
    setAuthed(false);
  };

  const setAvailable = async (id) => {
    const val = parseFloat(edits[id]);
    if (Number.isNaN(val)) return;
    await cincApi.put(`/cinc/items/${id}`, { available_kg: val });
    setEdits((p) => ({ ...p, [id]: undefined }));
    load();
  };
  const addKg = async (id, kg) => {
    await cincApi.put(`/cinc/items/${id}`, { add_kg: kg });
    load();
  };
  const removeItem = async (id) => {
    if (!window.confirm("Delete this fabric/colour?")) return;
    await cincApi.delete(`/cinc/items/${id}`);
    load();
  };
  const createItem = async (e) => {
    e.preventDefault();
    if (!newItem.fabric.trim()) return;
    await cincApi.post("/cinc/items", {
      fabric: newItem.fabric.trim(),
      color: newItem.color,
      available_kg: parseFloat(newItem.available_kg) || 0,
    });
    setNewItem({ fabric: "", color: ALL_COLORS[0], available_kg: "100" });
    load();
  };

  const cancelBooking = async (id) => {
    if (!window.confirm("Cancel this booking and return the fabric to stock?")) return;
    const { data } = await cincApi.delete(`/cinc/bookings/${id}`);
    load();
    return data;
  };

  const saveThreshold = async () => {
    const val = parseFloat(thresholdInput);
    if (Number.isNaN(val)) return;
    await cincApi.put("/cinc/settings", { low_stock_kg: val });
    load();
  };

  const exportCsv = async () => {
    const res = await cincApi.get("/cinc/bookings/export", { responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = "cinc_bookings.csv";
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const lowStockItems = useMemo(
    () => items.filter((i) => i.available_kg > 0 && i.available_kg <= threshold),
    [items, threshold]
  );
  const outItems = useMemo(() => items.filter((i) => i.available_kg <= 0), [items]);

  const grouped = useMemo(() => {
    const g = {};
    items.forEach((i) => (g[i.fabric] = g[i.fabric] || []).push(i));
    return g;
  }, [items]);

  const font = { fontFamily: "'IBM Plex Sans', system-ui, sans-serif" };

  if (!authed) {
    return (
      <div className="min-h-screen bg-[#1A1A17] text-[#F4F1EA] flex items-center justify-center px-5" style={font}>
        <link href="https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;500&family=IBM+Plex+Sans:wght@400;500;600;700&family=Fraunces:opsz,wght@9..144,600&display=swap" rel="stylesheet" />
        <form onSubmit={doLogin} className="w-full max-w-sm">
          <p style={{ fontFamily: "'Fraunces', serif" }} className="text-5xl font-semibold text-center">CiNC</p>
          <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#C9B98F] text-center mt-2 mb-10">Admin Console</p>
          <label className="font-mono text-[10px] uppercase tracking-widest text-[#C9B98F]">Password</label>
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="cinc-admin-password" autoFocus
            className="w-full mt-1 bg-transparent border border-[#3a3934] rounded-lg px-4 py-3 outline-none focus:border-[#C9B98F]" />
          {loginErr && <p className="text-red-400 text-sm mt-3" data-testid="cinc-login-error">{loginErr}</p>}
          <button type="submit" data-testid="cinc-admin-login" className="w-full mt-6 py-3 rounded-lg bg-[#C9B98F] text-[#1A1A17] font-semibold hover:bg-[#d9cba3]">Enter</button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F4F1EA] text-[#1A1A17]" style={font}>
      <link href="https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;500;600&family=IBM+Plex+Sans:wght@400;500;600;700&family=Fraunces:opsz,wght@9..144,600&display=swap" rel="stylesheet" />
      <header className="bg-[#1A1A17] text-[#F4F1EA]">
        <div className="max-w-[1400px] mx-auto px-5 md:px-8 py-4 flex items-center justify-between">
          <div className="flex items-baseline gap-3">
            <span style={{ fontFamily: "'Fraunces', serif" }} className="text-2xl font-semibold">CiNC</span>
            <span className="font-mono text-[10px] uppercase tracking-widest text-[#C9B98F]">Admin</span>
          </div>
          <button onClick={doLogout} data-testid="cinc-admin-logout" className="font-mono text-[11px] uppercase tracking-widest text-[#C9B98F] hover:text-white">Sign out</button>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-5 md:px-8 py-8">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
          {[
            ["Total available", summary ? `${fmt(summary.total_available_kg)} kg` : "—"],
            ["Booked", summary ? `${fmt(summary.booked_kg)} kg` : "—"],
            ["Varieties", summary ? summary.item_count : "—"],
            ["Bookings", bookings.length],
            ["Low / Out", `${lowStockItems.length} / ${outItems.length}`],
          ].map(([k, v]) => (
            <div key={k} className="bg-white border border-[#E4DFD3] rounded-lg p-4">
              <p className="font-mono text-[10px] uppercase tracking-widest text-[#8A8577]">{k}</p>
              <p style={{ fontFamily: "'Fraunces', serif" }} className="text-2xl md:text-3xl font-semibold mt-1">{v}</p>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap gap-1 border-b border-[#E4DFD3] mb-6">
          {[["inventory", "Inventory"], ["alerts", `Alerts${lowStockItems.length + outItems.length ? ` (${lowStockItems.length + outItems.length})` : ""}`], ["bookings", "Bookings"], ["add", "Add fabric"]].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`cinc-tab-${k}`}
              className={`px-5 py-2.5 text-sm font-medium border-b-2 -mb-px ${tab === k ? "border-[#1A1A17] text-[#1A1A17]" : "border-transparent text-[#8A8577]"}`}>{l}</button>
          ))}
        </div>

        {tab === "inventory" && (
          <div className="space-y-8">
            {Object.keys(grouped).sort().map((fab) => (
              <div key={fab}>
                <h3 style={{ fontFamily: "'Fraunces', serif" }} className="text-lg font-semibold mb-3">{fab}</h3>
                <div className="bg-white border border-[#E4DFD3] rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-[#E4DFD3] text-left">
                        <th className="px-4 py-2 font-mono text-[10px] uppercase tracking-widest text-[#8A8577]">Colour</th>
                        <th className="px-4 py-2 font-mono text-[10px] uppercase tracking-widest text-[#8A8577]">Available</th>
                        <th className="px-4 py-2 font-mono text-[10px] uppercase tracking-widest text-[#8A8577]">Set kg</th>
                        <th className="px-4 py-2 font-mono text-[10px] uppercase tracking-widest text-[#8A8577] text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {grouped[fab].map((it) => (
                        <tr key={it.id} className="border-b border-[#F0ECE1] last:border-0" data-testid={`cinc-admin-item-${it.id}`}>
                          <td className="px-4 py-2"><div className="flex items-center gap-2"><Swatch color={it.color} />{it.color}</div></td>
                          <td className="px-4 py-2 font-mono">{fmt(it.available_kg)} / {fmt(it.initial_kg)} kg</td>
                          <td className="px-4 py-2">
                            <div className="flex items-center gap-2">
                              <input value={edits[it.id] ?? ""} onChange={(e) => setEdits((p) => ({ ...p, [it.id]: e.target.value }))} placeholder={fmt(it.available_kg)} data-testid={`cinc-set-input-${it.id}`}
                                className="w-20 border border-[#E4DFD3] rounded px-2 py-1 outline-none focus:border-[#1A1A17]" />
                              <button onClick={() => setAvailable(it.id)} data-testid={`cinc-set-${it.id}`} className="px-2 py-1 rounded bg-[#1A1A17] text-white text-xs">Set</button>
                            </div>
                          </td>
                          <td className="px-4 py-2">
                            <div className="flex items-center justify-end gap-2">
                              <button onClick={() => addKg(it.id, 25)} className="px-2 py-1 rounded border border-[#D8D3C6] text-xs" data-testid={`cinc-add25-${it.id}`}>+25</button>
                              <button onClick={() => addKg(it.id, 20)} className="px-2 py-1 rounded border border-[#D8D3C6] text-xs">+20</button>
                              <button onClick={() => removeItem(it.id)} className="px-2 py-1 rounded border border-red-200 text-red-600 text-xs" data-testid={`cinc-del-${it.id}`}>Delete</button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "alerts" && (
          <div className="space-y-6">
            <div className="bg-white border border-[#E4DFD3] rounded-lg p-5 flex flex-wrap items-end gap-4">
              <div>
                <label className="font-mono text-[10px] uppercase tracking-widest text-[#8A8577]">Low-stock threshold (kg)</label>
                <p className="text-xs text-[#8A8577] mt-1 max-w-md">Any fabric-colour at or below this many kg is flagged so you know when to reorder.</p>
              </div>
              <div className="flex items-center gap-2">
                <input type="number" step="0.5" value={thresholdInput} onChange={(e) => setThresholdInput(e.target.value)} data-testid="cinc-threshold-input"
                  className="w-24 border border-[#E4DFD3] rounded-lg px-3 py-2 outline-none focus:border-[#1A1A17]" />
                <button onClick={saveThreshold} data-testid="cinc-threshold-save" className="px-4 py-2 rounded-lg bg-[#1A1A17] text-[#F4F1EA] text-sm">Save</button>
              </div>
            </div>

            <div className="bg-white border border-[#E4DFD3] rounded-lg overflow-hidden">
              <div className="px-4 py-3 border-b border-[#E4DFD3] font-mono text-[10px] uppercase tracking-widest text-[#8A8577]">
                Needs attention — {lowStockItems.length} low, {outItems.length} sold out
              </div>
              {[...outItems, ...lowStockItems].length === 0 ? (
                <p className="px-4 py-10 text-center text-[#8A8577]" style={{ fontFamily: "'Fraunces', serif" }}>Everything is well stocked. 👍</p>
              ) : (
                <table className="w-full text-sm">
                  <tbody>
                    {[...outItems, ...lowStockItems].map((it) => (
                      <tr key={it.id} className="border-b border-[#F0ECE1] last:border-0" data-testid={`cinc-alert-${it.id}`}>
                        <td className="px-4 py-3"><div className="flex items-center gap-2"><Swatch color={it.color} size={16} />{it.fabric} · {it.color}</div></td>
                        <td className="px-4 py-3 font-mono">{fmt(it.available_kg)} kg</td>
                        <td className="px-4 py-3">
                          <span className={`font-mono text-[10px] uppercase px-2 py-1 rounded ${it.available_kg <= 0 ? "bg-red-100 text-red-700" : "bg-orange-100 text-orange-700"}`}>
                            {it.available_kg <= 0 ? "Sold out" : "Low stock"}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button onClick={() => addKg(it.id, 25)} className="px-2 py-1 rounded border border-[#D8D3C6] text-xs mr-2" data-testid={`cinc-alert-refill-${it.id}`}>+25 kg</button>
                          <button onClick={() => addKg(it.id, 20)} className="px-2 py-1 rounded border border-[#D8D3C6] text-xs">+20 kg</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        {tab === "bookings" && (
          <div>
            <div className="flex justify-end mb-3">
              <button onClick={exportCsv} disabled={bookings.length === 0} data-testid="cinc-export-csv" className="px-4 py-2 rounded-lg bg-[#1A1A17] text-[#F4F1EA] text-sm disabled:opacity-50">Export CSV</button>
            </div>
            <div className="bg-white border border-[#E4DFD3] rounded-lg overflow-x-auto">
            <table className="w-full text-sm min-w-[800px]">
              <thead>
                <tr className="border-b border-[#E4DFD3] text-left">
                  {["Client", "Company", "Phone", "Fabric", "Colour", "Qty", "When", ""].map((h, i) => (
                    <th key={i} className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-[#8A8577]">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {bookings.length === 0 && <tr><td colSpan={8} className="px-4 py-8 text-center text-[#8A8577]" style={{ fontFamily: "'Fraunces', serif" }}>No bookings yet.</td></tr>}
                {bookings.map((b) => (
                  <tr key={b.id} className="border-b border-[#F0ECE1] last:border-0" data-testid={`cinc-booking-${b.id}`}>
                    <td className="px-4 py-3 font-medium">{b.client_name}</td>
                    <td className="px-4 py-3">{b.company_name || "—"}</td>
                    <td className="px-4 py-3 font-mono">{b.phone}</td>
                    <td className="px-4 py-3">{b.fabric}</td>
                    <td className="px-4 py-3"><div className="flex items-center gap-2"><Swatch color={b.color} size={16} />{b.color}</div></td>
                    <td className="px-4 py-3 font-mono">{fmt(b.quantity_kg)} kg</td>
                    <td className="px-4 py-3 font-mono text-xs text-[#8A8577]">{new Date(b.created_at).toLocaleString()}</td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => cancelBooking(b.id)} data-testid={`cinc-cancel-${b.id}`} className="px-2 py-1 rounded border border-red-200 text-red-600 text-xs">Cancel</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>
        )}

        {tab === "add" && (
          <form onSubmit={createItem} className="bg-white border border-[#E4DFD3] rounded-lg p-6 max-w-lg space-y-4">
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-[#8A8577]">Fabric name</label>
              <input value={newItem.fabric} onChange={(e) => setNewItem({ ...newItem, fabric: e.target.value })} data-testid="cinc-new-fabric" placeholder="e.g. Fabric K or Cotton Rib"
                className="w-full mt-1 border border-[#E4DFD3] rounded-lg px-4 py-2.5 outline-none focus:border-[#1A1A17]" />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-[#8A8577]">Colour</label>
              <select value={newItem.color} onChange={(e) => setNewItem({ ...newItem, color: e.target.value })} data-testid="cinc-new-color" className="w-full mt-1 border border-[#E4DFD3] rounded-lg px-4 py-2.5 outline-none">
                {ALL_COLORS.map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-widest text-[#8A8577]">Initial quantity (kg)</label>
              <input type="number" step="0.5" value={newItem.available_kg} onChange={(e) => setNewItem({ ...newItem, available_kg: e.target.value })} data-testid="cinc-new-kg"
                className="w-full mt-1 border border-[#E4DFD3] rounded-lg px-4 py-2.5 outline-none focus:border-[#1A1A17]" />
            </div>
            <button type="submit" data-testid="cinc-add-item" className="w-full py-3 rounded-lg bg-[#1A1A17] text-[#F4F1EA] font-medium">Add to inventory</button>
          </form>
        )}
      </div>
    </div>
  );
};

export default CincAdmin;
